/**
 * Convierte el `detallesJson` crudo de un evento de auditoría en una lista
 * de pares "Etiqueta legible · Valor formateado" entendible por un usuario
 * NO técnico (auditor de cumplimiento, contador, etc.).
 *
 * Transformaciones:
 *   1. Claves camelCase → frases en español.
 *      Ej. `numeroSolicitud` → "Número de solicitud".
 *   2. Valores enum del backend → etiquetas humanas.
 *      Ej. `EnAutorizacionNivel1` → "En autorización · nivel 1".
 *   3. GUIDs → mostrados como "ID interno: dd9379f2…" (truncado).
 *   4. Booleanos → "Sí" / "No".
 *   5. Fechas ISO → formato local México.
 *   6. Montos numéricos en campos sensibles → con miles + 2 decimales.
 *   7. Campos técnicos irrelevantes para auditor → ocultos por default.
 */

/** Un par etiqueta–valor listo para mostrar en la UI. */
export interface DetalleAmigable {
  /** Texto legible en español que describe el campo. */
  etiqueta: string;
  /** Valor ya formateado · siempre string para renderizar directamente. */
  valor: string;
  /** Sub-texto opcional · útil para mostrar el valor crudo del enum o el GUID completo. */
  sub?: string;
}

/**
 * Diccionario de claves técnicas → etiquetas legibles.
 * Si una clave no está mapeada se aplica `humanizarClave` como fallback.
 */
const ETIQUETAS_CLAVES: Record<string, string> = {
  id: "Identificador",
  numeroSolicitud: "Número de solicitud",
  folio: "Folio",
  estado: "Estado",
  estadoAntes: "Estado anterior",
  estadoDespues: "Nuevo estado",
  nivelActual: "Nivel actual",
  cantidadNiveles: "Total de niveles",
  nivelesCongelados: "Niveles del flujo",
  motivos: "Motivos",
  motivo: "Motivo",
  comentario: "Comentario",
  comentarios: "Comentarios",
  rfc: "RFC",
  rfcEmisor: "RFC del emisor",
  rfcReceptor: "RFC del receptor",
  razonSocial: "Razón social",
  numeroProveedor: "Número de proveedor",
  proveedorId: "Proveedor",
  organizacionId: "Organización",
  organizacionNombre: "Organización",
  organizacionRfc: "RFC de la organización",
  organizacionNumeroSociedad: "Número de sociedad",
  areaId: "Área",
  areaNombre: "Área",
  uuid: "UUID del CFDI",
  uuidCfdi: "UUID del CFDI",
  total: "Total",
  subtotal: "Subtotal",
  moneda: "Moneda",
  tipoComprobante: "Tipo de comprobante",
  tipo: "Tipo",
  subtipo: "Subtipo",
  fechaEmision: "Fecha de emisión",
  fechaInicio: "Fecha de inicio",
  fechaUltimoEvento: "Última actividad",
  ocurridoAt: "Cuándo ocurrió",
  cargadoAt: "Cargado el",
  autorizadoAt: "Autorizado el",
  enviadoAt: "Enviado el",
  iniciadoAt: "Iniciado el",
  categoria: "Categoría",
  categoriaDocumento: "Tipo de documento",
  nombreOriginal: "Nombre del archivo",
  extension: "Extensión",
  tamanoBytes: "Tamaño",
  numeroCfdi: "Número de CFDI",
  numeroSlot: "Número de CFDI",
  numeroLinea: "Línea",
  claveSat: "Clave SAT",
  centroDeCosto: "Centro de costo",
  cuentaContable: "Cuenta contable",
  importe: "Importe",
  cantidad: "Cantidad",
  email: "Correo electrónico",
  emailUsuario: "Correo del usuario",
  nombre: "Nombre",
  descripcion: "Descripción",
  reglas: "Reglas",
  codigo: "Código",
  mensaje: "Mensaje",
  enviadaAContabilizacion: "¿Enviada a contabilización?",
  esActivo: "¿Está activo?",
  estaActivo: "¿Está activo?",
  // --- Auditoría del prorrateo (snapshots de líneas) ---
  antes: "Antes",
  despues: "Después",
  lineaAgregada: "Línea agregada",
  lineaEliminada: "Línea eliminada",
  lineasAlReabrir: "Líneas al momento de reabrir",
  lineas: "Líneas",
  lineasCount: "Total de líneas",
  totalLineas: "Total de líneas",
  sumaImportes: "Suma de importes",
  subtotalCfdi: "Subtotal del CFDI",
  claveSatDescripcion: "Descripción de clave SAT",
};

/**
 * Diccionario de valores enum → etiquetas humanas.
 * Cubre los estados de SolicitudDePago + categorías de documentos + acciones.
 */
const ETIQUETAS_VALORES: Record<string, string> = {
  // Estados de solicitud
  Iniciada: "Iniciada",
  TipoSeleccionado: "Tipo seleccionado",
  SubtipoSeleccionado: "Subtipo seleccionado",
  CfdiPendiente: "Esperando carga de CFDI",
  CfdiCargado: "CFDI cargado",
  EnValidacionReglas: "Validando reglas RN1-RN10",
  ReglasRechazadas: "Reglas rechazadas",
  EnValidacionFiscal: "Esperando respuesta del SAT",
  Validado: "Validado",
  RechazadoFiscal: "Rechazado por el SAT",
  SuspendidaFiscal: "Suspendida por el SAT",
  CapturandoDatos: "Capturando datos generales",
  SeleccionandoProveedor: "Seleccionando proveedor",
  Prorrateando: "Capturando prorrateo",
  ProvisionalmenteListo: "Lista para documentos",
  AdjuntandoDocumentos: "Adjuntando documentos",
  ListoParaAutorizacion: "Lista para autorización",
  EnAutorizacionNivel1: "En autorización · nivel 1",
  EnAutorizacionNivelN: "En autorización · nivel siguiente",
  RechazadaParaCorreccion: "Rechazada para corrección",
  Autorizada: "Autorizada",
  EnviadaAContabilizacion: "Enviada a contabilización",
  PolizaGenerada: "Póliza generada",
  EnColaContabilizacion: "En cola de contabilización",
  DadaDeBajaPorAutorizador: "Baja por Mesa de Control",
  CanceladaPorSolicitante: "Cancelada por el solicitante",
  DescartadaPorPlazoVencido: "Descartada · plazo vencido",
  // Categorías de documento
  FacturaXml: "Factura XML",
  FacturaPdf: "Factura PDF",
  Evidencia: "Evidencia",
  Contrato: "Contrato",
  Adicional: "Documento adicional",
  // Tipos de solicitud
  FB60: "FB60 (factura)",
  CFDI_UNICO: "CFDI único",
  MULTIPLES_CFDI: "Múltiples CFDI",
};

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/**
 * Convierte un detallesJson crudo en una lista de pares amigables.
 * Si el JSON es inválido, devuelve el texto crudo como único item.
 */
export function formatearDetallesAmigables(raw: string | null | undefined): DetalleAmigable[] {
  if (!raw) return [];
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return [{ etiqueta: "Detalles", valor: raw }];
  }
  if (typeof obj !== "object" || obj === null) {
    return [{ etiqueta: "Detalles", valor: String(obj) }];
  }
  return aplanarObjeto(obj as Record<string, unknown>);
}

function aplanarObjeto(obj: Record<string, unknown>, prefijo = ""): DetalleAmigable[] {
  const items: DetalleAmigable[] = [];
  for (const [clave, valorRaw] of Object.entries(obj)) {
    // Saltar campos puramente técnicos (`id` de GUID a la raíz · ya está
    // disponible en la columna Entidad del listado).
    if (!prefijo && clave === "id" && esGuid(valorRaw)) continue;

    const etiqueta = etiquetaDeClave(clave, prefijo);
    items.push(...convertirValor(etiqueta, clave, valorRaw));
  }
  return items;
}

function convertirValor(
  etiqueta: string,
  clave: string,
  valor: unknown
): DetalleAmigable[] {
  if (valor === null || valor === undefined) {
    return [{ etiqueta, valor: "—" }];
  }
  if (typeof valor === "boolean") {
    return [{ etiqueta, valor: valor ? "Sí" : "No" }];
  }
  if (typeof valor === "number") {
    // Heurística · si la clave parece monto, formatear con miles.
    if (esCampoMonetario(clave)) {
      return [
        {
          etiqueta,
          valor: valor.toLocaleString("es-MX", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        },
      ];
    }
    return [{ etiqueta, valor: valor.toLocaleString("es-MX") }];
  }
  if (typeof valor === "string") {
    if (esGuid(valor)) {
      return [
        {
          etiqueta,
          valor: "Referencia interna",
          sub: valor.slice(0, 8) + "…",
        },
      ];
    }
    if (ISO_DATE_REGEX.test(valor)) {
      const fecha = new Date(valor);
      if (!isNaN(fecha.getTime())) {
        return [
          {
            etiqueta,
            valor: fecha.toLocaleString("es-MX", {
              day: "2-digit",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          },
        ];
      }
    }
    return [{ etiqueta, valor: traducirValorEnum(valor) }];
  }
  if (Array.isArray(valor)) {
    if (valor.length === 0) return [{ etiqueta, valor: "(vacío)" }];
    if (valor.every((v) => typeof v === "string" || typeof v === "number")) {
      return [{ etiqueta, valor: valor.map((v) => traducirValorEnum(String(v))).join(", ") }];
    }
    // Array de objetos · aplanar con índice.
    return valor.flatMap((item, idx) => {
      if (typeof item === "object" && item !== null) {
        return aplanarObjeto(item as Record<string, unknown>, `${etiqueta} #${idx + 1}`).map(
          (d) => ({ ...d, etiqueta: `${etiqueta} #${idx + 1} · ${d.etiqueta}` })
        );
      }
      return [{ etiqueta: `${etiqueta} #${idx + 1}`, valor: String(item) }];
    });
  }
  if (typeof valor === "object") {
    // Objeto anidado · aplanar con prefijo.
    return aplanarObjeto(valor as Record<string, unknown>, etiqueta).map((d) => ({
      ...d,
      etiqueta: `${etiqueta} · ${d.etiqueta}`,
    }));
  }
  return [{ etiqueta, valor: String(valor) }];
}

function etiquetaDeClave(clave: string, prefijo: string): string {
  const traducida = ETIQUETAS_CLAVES[clave] ?? humanizarClave(clave);
  return prefijo ? traducida : traducida;
}

/**
 * Convierte una clave camelCase / snake_case en una frase legible:
 *   `numeroSolicitud` → "Número solicitud"
 *   `area_nombre`     → "Area nombre"
 * (Fallback cuando no hay traducción curada en ETIQUETAS_CLAVES.)
 */
function humanizarClave(clave: string): string {
  const conEspacios = clave
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
}

function traducirValorEnum(valor: string): string {
  return ETIQUETAS_VALORES[valor] ?? valor;
}

function esGuid(v: unknown): v is string {
  return typeof v === "string" && GUID_REGEX.test(v);
}

function esCampoMonetario(clave: string): boolean {
  const k = clave.toLowerCase();
  return (
    k.includes("total") ||
    k.includes("subtotal") ||
    k.includes("importe") ||
    k.includes("monto") ||
    k === "precio"
  );
}
